// Pulls a real call into the Add-call form: its transcript and a golden SEEDED from the
// call's current AI extraction. A seed is only the old model's answer, so a person must
// correct it before the item can measure accuracy (the golden editor opens on it).

import { CALL_TRANSCRIPTION_QUERY } from 'app/graphql/callTranscription'
import { gqlFetchOne, gqlQuery } from 'app/lib/graphqlClient'
import type { GoldenExtraction, GoldenPerson } from './mockData'

export type ImportedCall = {
  /** ISO timestamp of the call. */
  callDate: string
  callerName: string
  /** Plain speaker lines, as the pipeline reads the transcript. */
  transcript: string
  golden: GoldenExtraction
}

/**
 * Converts an SRT transcript to plain speaker lines. The pipeline reads the stored `txt`
 * transcript, but the only transcript endpoint serves SRT; dropping the cue numbers and
 * timestamps is the closest available match.
 */
export function srtToText(srt: string): string {
  return srt
    .replace(/\r/g, ``)
    .split(/\n\s*\n/)
    .map((block) =>
      block
        .split(`\n`)
        .filter((line) => !/^\s*\d+\s*$/.test(line) && !line.includes(`-->`))
        .join(` `)
        .trim(),
    )
    .filter(Boolean)
    .join(`\n`)
}

export async function importCall(callId: string): Promise<ImportedCall> {
  const call = await gqlFetchOne(`call`, callId)
  if (!call) throw new Error(`Call ${callId} was not found`)

  const { data, error } = await gqlQuery(CALL_TRANSCRIPTION_QUERY, { id: callId })
  if (error) throw new Error(`Transcript request failed: ${error.message}`)
  const transcript = srtToText(data?.callTranscription ?? ``)
  if (!transcript) throw new Error(`Call ${callId} has no transcript`)

  const people: GoldenPerson[] = (call.ai?.people ?? []).map((person) => ({
    name: person.name ?? ``,
    isCaller: person.isCaller ?? false,
    patientType: person.patientType ?? `Other`,
    relationship: person.relationship || undefined,
    phone: person.phone || undefined,
    email: person.email || undefined,
    birthDate: person.birthDate || undefined,
    appointmentRequested: person.appointmentRequested ?? false,
    appointmentScheduled: person.appointmentScheduled ?? false,
    appointmentDate: person.appointmentDate ?? undefined,
    appointmentTime: person.appointmentTime ?? undefined,
    treatment: person.treatment || undefined,
    serviceRequested: person.serviceRequested || undefined,
    reasonForNotScheduling: person.reasonForNotScheduling || undefined,
    outcomes: person.outcomes ?? [],
    insuranceProvider: person.insuranceProvider || undefined,
    referral: person.referral || undefined,
  }))

  return {
    callDate: call.callDate ?? new Date().toISOString(),
    callerName: people.find((person) => person.isCaller)?.name || `Unknown Caller`,
    transcript,
    golden: { answerer: call.ai?.answerer ?? undefined, channel: call.ai?.channel ?? undefined, people },
  }
}
